import { Add } from '@mui/icons-material';
import { Box, Snackbar } from '@mui/material';
import React, { useState } from 'react';
import { useGetAllNotes } from '../../services/react-query/notes';
import { Noteable } from '../../types/Note';
import { UserSettingsCollapseKeys } from '../../types/User';
import { Loading } from '../../uikit';
import ResponsiveButton from '../../uikit/buttons/ResponsiveButton';
import NoteModal from './NoteModal';
import NotesList from './NotesList';
import { noteTableWrapper } from './noteStyles';
import AccordionSection from '../AccordionSection';

interface INotesSectionProps {
  noteable: Noteable;
  collapseId: UserSettingsCollapseKeys;
}

const NotesSection: React.FC<INotesSectionProps> = ({ noteable, collapseId }) => {
  const { noteableUuid, noteableType } = noteable;
  const [noteSnackbarMessage, setNoteSnackbarMessage] = useState<string | null>(null);
  const [noteModalContent, setNoteModalContent] = useState<React.ReactNode | null>(null);
  const [expandedNoteUuids, setExpandedNoteUuids] = useState<string[]>([]); // Initialize as an empty array

  const { data: notesData, isLoading: isGetAllNotesLoading } = useGetAllNotes({
    params: {
      filters: {
        noteable_uuid: noteableUuid,
        noteable_type: noteableType,
      },
    },
  });

  if (noteableUuid === 'new') return null;

  if (isGetAllNotesLoading) {
    return <Loading />;
  }

  const notes = notesData?.data || [];

  const handleNoteAction = (uuid: string, parentUuid?: string) => {
    setNoteModalContent(
      <NoteModal
        noteable={noteable}
        setNoteSnackbarMessage={setNoteSnackbarMessage}
        uuid={uuid}
        parentUuid={parentUuid}
        isOpen={true}
        setExpandedNoteUuid={(uuid) => {
          if (uuid && !expandedNoteUuids.includes(uuid)) {
            setExpandedNoteUuids((prev) => [...prev, uuid]);
          }
        }}
        onCancel={() => {
          setNoteModalContent(null);
        }}
      />
    );
  };

  const noteTableActionButtons = (
    <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => handleNoteAction('new')}>
      Add
    </ResponsiveButton>
  );

  return (
    <AccordionSection title="Notes" headerElements={noteTableActionButtons} collapseId={collapseId} id="notes">
      <Box sx={noteTableWrapper}>
        <NotesList
          notes={notes}
          handleNoteEdit={(note) => note.uuid && handleNoteAction(note.uuid)}
          handleReply={(parentUuid) => handleNoteAction('new', parentUuid)}
          expandedNoteUuids={expandedNoteUuids}
          setExpandedNoteUuids={setExpandedNoteUuids}
        />
      </Box>
      <Snackbar open={!!noteSnackbarMessage} onClose={() => setNoteSnackbarMessage(null)} message={noteSnackbarMessage} />
      {noteModalContent as React.ReactElement}
    </AccordionSection>
  );
};

export default NotesSection;
